import { useEffect, useState } from "react";

/**
 * Returns a value that is debounced by `delay` based on `value`.
 *
 * Based on https://usehooks-ts.com/react-hook/use-debounce
 *
 * A common use case is to debounce a search string that your component has in local state.
 *
 * @example
 *
 * // `appliedSearch` is only updated when `search` is unchanged for 150ms.
 * const [search, setSearch] = useState<string>('');
 * const appliedSearch = useDebounce(search, 150);
 */
const useDebounce = <T>(value: T, delay?: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default useDebounce;
